/**
 * useHaptics - Sistema de Haptic Feedback (Vibração)
 *
 * Sincroniza vibrações do dispositivo com eventos do jogo:
 * - Tiros
 * - Impactos
 * - Explosões
 * - Dano recebido
 * - Coleta de items
 * - Vitória/Derrota
 *
 * No Capacitor (mobile), usa @capacitor/haptics para feedback nativo.
 * No web, usa navigator.vibrate() como fallback.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const Capacitor = { isNativePlatform: () => false };

interface HapticsState {
  enabled: boolean;
  intensity: 'low' | 'medium' | 'high';
  isSupported: boolean;

  // Ações
  setEnabled: (enabled: boolean) => void;
  setIntensity: (intensity: 'low' | 'medium' | 'high') => void;
  testVibration: () => void;

  // Haptic triggers
  vibrateShoot: () => void;
  vibrateImpact: () => void;
  vibrateExplosion: () => void;
  vibrateDamage: () => void;
  vibrateCollect: () => void;
  vibrateVictory: () => void;
  vibrateDefeat: () => void;
  vibrateElectricShock: () => void;
  vibrateZoneShrink: () => void;

  // Interface — feedback de navegação e confirmação nos menus
  vibrateUiTap: () => void;
  vibrateUiSelect: () => void;

  // Custom vibration
  vibrate: (pattern: number | number[]) => void;
}

// Verificar suporte a vibração
const hasVibrationSupport = (): boolean => {
  if (Capacitor.isNativePlatform()) return true;
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

// Cache do módulo haptics (lazy loaded, web fallback only)
let _Haptics: any = null;
let _ImpactStyle: any = null;
let _NotificationType: any = null;
let _hapticsReady: Promise<void> | null = null;

function loadCapacitorHaptics(): Promise<void> {
  if (_hapticsReady) return _hapticsReady;
  _hapticsReady = Promise.resolve();
  return _hapticsReady;
}

// Pre-carregar no native (o Promise eh armazenado para reutilizacao)
if (Capacitor.isNativePlatform()) {
  loadCapacitorHaptics();
}

// Multiplicadores de intensidade
const intensityMultiplier = {
  low: 0.5,
  medium: 1,
  high: 1.5,
};

// Padrões de vibração base (em ms) - usados no fallback web
const patterns = {
  shoot: [30],                           // Curto e seco
  impact: [50],                          // Impacto médio
  explosion: [100, 50, 100, 50, 150],   // Sequência explosiva
  damage: [100, 30, 100],               // Dano recebido
  collect: [20, 20, 20],                // Coleta rápida
  victory: [100, 100, 100, 100, 300],   // Celebração
  defeat: [300, 100, 500],              // Derrota dramática
  electricShock: [20, 10, 20, 10, 30, 10, 50], // Choque elétrico pulsante
  zoneShrink: [50, 30, 50, 30, 100],    // Aviso de zona diminuindo
  uiTap: [12],                          // Toque de navegação — quase subliminar
  uiSelect: [18, 24, 12],               // Confirmação de escolha
};

// Mapping de intensidade para Capacitor ImpactStyle
type CapacitorIntensity = 'light' | 'medium' | 'heavy';
const intensityToCapacitor: Record<string, CapacitorIntensity> = {
  low: 'light',
  medium: 'medium',
  high: 'heavy',
};

// Aplicar multiplicador ao padrão (para web fallback)
const applyIntensity = (pattern: number[], multiplier: number): number[] => {
  return pattern.map(duration => Math.round(duration * multiplier));
};

/**
 * Garante que o modulo de haptics esta carregado antes de usar.
 * Se o modulo ainda nao carregou (race condition no startup),
 * tenta carregar e executa o callback quando pronto.
 */
function withHaptics(fn: () => void): void {
  if (_Haptics && _ImpactStyle && _NotificationType) {
    fn();
  } else if (Capacitor.isNativePlatform()) {
    // Modulo ainda carregando - espera e executa
    loadCapacitorHaptics().then(() => {
      if (_Haptics) fn();
    });
  }
}

/**
 * Executa haptic feedback nativo no Capacitor.
 */
function nativeImpact(style: CapacitorIntensity): void {
  withHaptics(() => {
    const styleMap = { light: _ImpactStyle!.Light, medium: _ImpactStyle!.Medium, heavy: _ImpactStyle!.Heavy };
    _Haptics!.impact({ style: styleMap[style] }).catch(() => {});
  });
}

function nativeNotification(type: 'success' | 'warning' | 'error'): void {
  withHaptics(() => {
    const typeMap = { success: _NotificationType!.Success, warning: _NotificationType!.Warning, error: _NotificationType!.Error };
    _Haptics!.notification({ type: typeMap[type] }).catch(() => {});
  });
}

/**
 * Vibra usando a melhor API disponível.
 */
function doVibrate(pattern: number[], intensity: 'low' | 'medium' | 'high'): void {
  if (Capacitor.isNativePlatform() && _Haptics) {
    // No native, usar Capacitor Haptics
    nativeImpact(intensityToCapacitor[intensity]);
  } else if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    // No web, usar Web Vibration API
    const multiplier = intensityMultiplier[intensity];
    navigator.vibrate(applyIntensity(pattern, multiplier));
  }
}

export const useHaptics = create<HapticsState>()(
  persist(
    (set, get) => ({
      enabled: true,
      intensity: 'medium',
      isSupported: hasVibrationSupport(),

      setEnabled: (enabled) => set({ enabled }),
      setIntensity: (intensity) => set({ intensity }),

      testVibration: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          nativeNotification('success');
        } else {
          navigator.vibrate([100, 50, 100]);
        }
      },

      vibrate: (pattern) => {
        const state = get();
        if (!state.enabled || !hasVibrationSupport()) return;

        try {
          if (Capacitor.isNativePlatform() && _Haptics) {
            nativeImpact(intensityToCapacitor[state.intensity]);
          } else if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
            const multiplier = intensityMultiplier[state.intensity];
            if (Array.isArray(pattern)) {
              navigator.vibrate(applyIntensity(pattern, multiplier));
            } else {
              navigator.vibrate(Math.round(pattern * multiplier));
            }
          }
        } catch (e) {
          console.debug('[haptics] Vibration not available:', e);
        }
      },

      vibrateShoot: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;
        doVibrate(patterns.shoot, intensity);
      },

      // Navegação de menu: pulso mínimo. No native usa sempre 'light'
      // independente da intensidade — feedback de UI que acompanha a
      // intensidade de combate fica desagradável em uso prolongado.
      vibrateUiTap: () => {
        const { enabled } = get();
        if (!enabled || !hasVibrationSupport()) return;
        if (Capacitor.isNativePlatform() && _Haptics) {
          nativeImpact('light');
        } else if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(patterns.uiTap);
        }
      },

      vibrateUiSelect: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;
        doVibrate(patterns.uiSelect, intensity);
      },

      vibrateImpact: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;
        doVibrate(patterns.impact, intensity);
      },

      vibrateExplosion: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          // Sequência de impactos para simular explosão
          nativeImpact('heavy');
          setTimeout(() => nativeImpact('medium'), 150);
          setTimeout(() => nativeImpact('heavy'), 300);
        } else {
          const multiplier = intensityMultiplier[intensity];
          navigator.vibrate(applyIntensity(patterns.explosion, multiplier));
        }
      },

      vibrateDamage: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          nativeNotification('error');
        } else {
          const multiplier = intensityMultiplier[intensity];
          navigator.vibrate(applyIntensity(patterns.damage, multiplier));
        }
      },

      vibrateCollect: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          nativeImpact('light');
        } else {
          const multiplier = intensityMultiplier[intensity];
          navigator.vibrate(applyIntensity(patterns.collect, multiplier));
        }
      },

      vibrateVictory: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          nativeNotification('success');
          setTimeout(() => nativeNotification('success'), 300);
        } else {
          const multiplier = intensityMultiplier[intensity];
          navigator.vibrate(applyIntensity(patterns.victory, multiplier));
        }
      },

      vibrateDefeat: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          nativeNotification('error');
          setTimeout(() => nativeImpact('heavy'), 300);
        } else {
          const multiplier = intensityMultiplier[intensity];
          navigator.vibrate(applyIntensity(patterns.defeat, multiplier));
        }
      },

      vibrateElectricShock: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          // Sequência rápida de pulsos leves
          nativeImpact('light');
          setTimeout(() => nativeImpact('light'), 30);
          setTimeout(() => nativeImpact('medium'), 60);
          setTimeout(() => nativeImpact('light'), 100);
        } else {
          const multiplier = intensityMultiplier[intensity];
          navigator.vibrate(applyIntensity(patterns.electricShock, multiplier));
        }
      },

      vibrateZoneShrink: () => {
        const { enabled, intensity } = get();
        if (!enabled || !hasVibrationSupport()) return;

        if (Capacitor.isNativePlatform() && _Haptics) {
          nativeNotification('warning');
        } else {
          const multiplier = intensityMultiplier[intensity];
          navigator.vibrate(applyIntensity(patterns.zoneShrink, multiplier));
        }
      },
    }),
    {
      name: 'rocketanks-haptics',
      partialize: (state) => ({
        enabled: state.enabled,
        intensity: state.intensity,
      }),
    }
  )
);

// Helper para verificar suporte
export const isHapticsSupported = hasVibrationSupport;

// Singleton para uso direto (sem hooks)
export const haptics = {
  shoot: () => useHaptics.getState().vibrateShoot(),
  impact: () => useHaptics.getState().vibrateImpact(),
  explosion: () => useHaptics.getState().vibrateExplosion(),
  damage: () => useHaptics.getState().vibrateDamage(),
  collect: () => useHaptics.getState().vibrateCollect(),
  victory: () => useHaptics.getState().vibrateVictory(),
  defeat: () => useHaptics.getState().vibrateDefeat(),
  electricShock: () => useHaptics.getState().vibrateElectricShock(),
  zoneShrink: () => useHaptics.getState().vibrateZoneShrink(),
  custom: (pattern: number | number[]) => useHaptics.getState().vibrate(pattern),
};
